import { z } from "zod";

import { HttpRequestPageSchema } from "@moment/api-collab/api-types";

import type { HttpRequestPage, QueryError, QueryResponse, QueryTiming } from "./request-page.types";

const QueryErrorCodeSchema = z.enum([
	"PAGE_NOT_FOUND",
	"PAGE_NOT_LOADED",
	"UNRESOLVED_VARS",
	"AUTH_ERROR",
	"AWS_LOGIN_REQUIRED",
	"NETWORK_ERROR",
	"TIMEOUT",
	"UNKNOWN",
]);

const QueryErrorSchema = z.object({
	code: QueryErrorCodeSchema,
	message: z.string(),
	status: z.number().optional(),
	responseBody: z.string().optional(),
	authHintDetails: z
		.object({
			cluster_name: z.string().optional(),
			region: z.string().optional(),
			account_id: z.string().optional(),
		})
		.optional(),
});

const QueryTimingSchema = z.object({
	startedAt: z.number().int(),
	endedAt: z.number().int(),
	durationMs: z.number().int(),
});

const HeaderTupleSchema = z.tuple([z.string(), z.string()]);

const HttpResponseSchema = z.object({
	kind: z.literal("http"),
	status: z.number().int(),
	statusText: z.string(),
	ok: z.boolean(),
	url: z.string(),
	headers: z.array(HeaderTupleSchema),
	parsedBody: z.unknown(),
	bodyEncoding: z.enum(["utf-8", "base64"]),
	bodySize: z.number().int(),
	auth_hint: z
		.object({
			provider: z.string(),
			cluster_name: z.string().optional(),
			region: z.string().optional(),
			account_id: z.string().optional(),
		})
		.optional(),
});

const PostgresColumnSchema = z.object({
	name: z.string(),
	dataType: z.string(),
});

const PostgresResponseBaseSchema = z.object({
	columns: z.array(PostgresColumnSchema),
	rows: z.array(z.record(z.string(), z.unknown())),
	rowCount: z.number().int(),
	command: z.string(),
	durationMs: z.number().int(),
});

const PostgresResponseSchema = PostgresResponseBaseSchema.extend({
	kind: z.literal("postgres"),
});

const SqliteResponseSchema = PostgresResponseBaseSchema.extend({
	kind: z.literal("sqlite"),
});

const JavaScriptResponseSchema = z.object({
	kind: z.literal("javascript"),
	parsedBody: z.unknown(),
	bodySize: z.number().int(),
	durationMs: z.number().int(),
});

const GrpcResponseSchema = z.object({
	kind: z.literal("grpc"),
	statusCode: z.number().int(),
	statusMessage: z.string(),
	messages: z.array(
		z.object({
			index: z.number().int(),
			body: z.unknown(),
			bodySize: z.number().int(),
		})
	),
	responseMetadata: z.array(HeaderTupleSchema),
	trailers: z.array(HeaderTupleSchema),
	durationMs: z.number().int(),
	streamType: z.enum(["unary", "server", "client", "bidi"]),
});

const QueryResponseSchema = z.discriminatedUnion("kind", [
	HttpResponseSchema,
	PostgresResponseSchema,
	SqliteResponseSchema,
	JavaScriptResponseSchema,
	GrpcResponseSchema,
]);

export const RequestPageHistoryStateSchema = HttpRequestPageSchema.extend({
	response: QueryResponseSchema.optional(),
	error: QueryErrorSchema.optional(),
	timing: QueryTimingSchema.optional(),
});

export const RequestPageHistoryEntrySchema = z.object({
	id: z.number().int(),
	createdAt: z.number().int(),
	state: RequestPageHistoryStateSchema,
});

export type RequestPageHistoryState = HttpRequestPage & {
	response?: QueryResponse;
	error?: QueryError;
	timing?: QueryTiming;
};

export type RequestPageHistoryEntry = {
	id: number;
	createdAt: number;
	state: RequestPageHistoryState;
};

export type RequestPageHistoryRow = {
	id: number;
	created_at: number;
	schemaVersion: number;
	kind: string;
	pageType: string;
	request: string;
	ui: string | null;
	response: string | null;
	error: string | null;
	timing: string | null;
};

export function serializeRequestPageHistoryState(
	state: RequestPageHistoryState,
	createdAt: number
): Omit<RequestPageHistoryRow, "id"> {
	return {
		created_at: createdAt,
		schemaVersion: state.schemaVersion,
		kind: state.kind,
		pageType: state.pageType,
		request: JSON.stringify(state.request),
		ui: state.ui ? JSON.stringify(state.ui) : null,
		response: state.response ? JSON.stringify(state.response) : null,
		error: state.error ? JSON.stringify(state.error) : null,
		timing: state.timing ? JSON.stringify(state.timing) : null,
	};
}

export function deserializeRequestPageHistoryRow(
	row: RequestPageHistoryRow
): RequestPageHistoryEntry | null {
	try {
		const parsed = RequestPageHistoryEntrySchema.safeParse({
			id: row.id,
			createdAt: row.created_at,
			state: {
				schemaVersion: row.schemaVersion,
				kind: row.kind,
				pageType: row.pageType,
				request: JSON.parse(row.request),
				ui: row.ui ? JSON.parse(row.ui) : undefined,
				response: row.response ? JSON.parse(row.response) : undefined,
				error: row.error ? JSON.parse(row.error) : undefined,
				timing: row.timing ? JSON.parse(row.timing) : undefined,
			},
		});

		return parsed.success ? (parsed.data as RequestPageHistoryEntry) : null;
	} catch {
		return null;
	}
}
